import type { SocialPlatform, SocialPostKind } from '@/lib/social-api';
import { TAG_BG, TAG_FILL } from '@/lib/tag-palette';

export interface PlatformMeta {
  id: SocialPlatform;
  label: string;
  /** Tailwind-Klassen für Badges/Chips (Light + Dark). */
  badgeClass: string;
  /** Volltonfarbe für Kalendereinträge. */
  dotClass: string;
  /** Hex-Farbe für Diagramme und Inline-Styles. */
  hex: string;
  /** Maximale Zeichenzahl des Haupttexts. */
  textLimit: number;
  /** Post-Arten, die dieser Kanal über die API unterstützt. */
  kinds: SocialPostKind[];
  /** Titel ist Pflicht (YouTube, Reddit). */
  titleRequired: boolean;
  /** Ohne Medium nicht veröffentlichbar. */
  mediaRequired: boolean;
  /** Kurzer Hinweis auf Besonderheiten des Kanals. */
  hint: string;
}

export const PLATFORMS: Record<SocialPlatform, PlatformMeta> = {
  linkedin: {
    id: 'linkedin',
    label: 'LinkedIn',
    badgeClass: TAG_FILL.blue,
    dotClass: TAG_BG.blue,
    hex: '#0a66c2',
    textLimit: 3000,
    kinds: ['feed', 'link', 'carousel'],
    titleRequired: false,
    mediaRequired: false,
    hint: 'Text-Posts funktionieren ohne Medien. Die ersten ~200 Zeichen entscheiden über den Klick auf „mehr“.',
  },
  instagram: {
    id: 'instagram',
    label: 'Instagram',
    badgeClass: TAG_FILL.pink,
    dotClass: TAG_BG.pink,
    hex: '#e1306c',
    textLimit: 2200,
    kinds: ['feed', 'story', 'reel', 'carousel'],
    titleRequired: false,
    mediaRequired: true,
    hint: 'Immer mit Bild oder Video. Medien brauchen eine öffentliche URL – Upload über die Medienbibliothek.',
  },
  facebook: {
    id: 'facebook',
    label: 'Facebook',
    badgeClass: TAG_FILL.indigo,
    dotClass: TAG_BG.indigo,
    hex: '#1877f2',
    textLimit: 63206,
    kinds: ['feed', 'story', 'reel', 'video', 'link'],
    titleRequired: false,
    mediaRequired: false,
    hint: 'Veröffentlicht auf der verbundenen Seite. Text-, Bild-, Video- und Link-Posts sind möglich.',
  },
  youtube: {
    id: 'youtube',
    label: 'YouTube',
    badgeClass: TAG_FILL.red,
    dotClass: TAG_BG.red,
    hex: '#ff0000',
    textLimit: 5000,
    kinds: ['video', 'short'],
    titleRequired: true,
    mediaRequired: true,
    hint: 'Nur Video. Titel max. 100 Zeichen; Shorts sollten 9:16 und unter 60 Sekunden sein.',
  },
  tiktok: {
    id: 'tiktok',
    label: 'TikTok',
    badgeClass: TAG_FILL.slate,
    dotClass: TAG_BG.slate,
    hex: '#010101',
    textLimit: 2200,
    kinds: ['video', 'carousel'],
    titleRequired: false,
    mediaRequired: true,
    hint: 'Video oder Foto-Post. Solange die App nicht auditiert ist, erscheinen Beiträge nur privat (SELF_ONLY).',
  },
  reddit: {
    id: 'reddit',
    label: 'Reddit',
    badgeClass: TAG_FILL.orange,
    dotClass: TAG_BG.orange,
    hex: '#ff4500',
    textLimit: 40000,
    kinds: ['text', 'link', 'feed'],
    titleRequired: true,
    mediaRequired: false,
    hint: 'Braucht Titel und Ziel-Subreddit. Beachte die Regeln des Subreddits – Werbung fliegt sonst raus.',
  },
  x: {
    id: 'x',
    label: 'X',
    badgeClass: TAG_FILL.violet,
    dotClass: TAG_BG.violet,
    hex: '#000000',
    textLimit: 280,
    kinds: ['feed', 'text', 'link'],
    titleRequired: false,
    mediaRequired: false,
    hint: '280 Zeichen inkl. Link. Bis zu 4 Medien; der erste Kommentar wird als Antwort gepostet.',
  },
  threads: {
    id: 'threads',
    label: 'Threads',
    badgeClass: TAG_FILL.teal,
    dotClass: TAG_BG.teal,
    hex: '#101010',
    textLimit: 500,
    kinds: ['feed', 'text', 'link', 'carousel', 'video'],
    titleRequired: false,
    mediaRequired: false,
    hint: '500 Zeichen. Medien brauchen eine öffentliche URL; mehrere Bilder werden zum Carousel.',
  },
  pinterest: {
    id: 'pinterest',
    label: 'Pinterest',
    badgeClass: TAG_FILL.amber,
    dotClass: TAG_BG.amber,
    hex: '#e60023',
    textLimit: 500,
    kinds: ['feed', 'link'],
    titleRequired: false,
    mediaRequired: true,
    hint: 'Jeder Pin braucht ein Bild und ein Ziel-Board. Hochformat (2:3) funktioniert am besten.',
  },
  google_business: {
    id: 'google_business',
    label: 'Google Business',
    badgeClass: TAG_FILL.green,
    dotClass: TAG_BG.green,
    hex: '#34a853',
    textLimit: 1500,
    kinds: ['feed', 'text', 'link'],
    titleRequired: false,
    mediaRequired: false,
    hint: 'Beitrag im Unternehmensprofil (Suche/Maps). Mit Link wird automatisch ein CTA-Button gesetzt.',
  },
};

export const PLATFORM_ORDER: SocialPlatform[] = [
  'linkedin',
  'instagram',
  'facebook',
  'youtube',
  'tiktok',
  'x',
  'threads',
  'pinterest',
  'reddit',
  'google_business',
];

export const POST_KIND_LABEL: Record<SocialPostKind, string> = {
  feed: 'Feed',
  story: 'Story',
  reel: 'Reel',
  short: 'Short',
  carousel: 'Carousel',
  video: 'Video',
  text: 'Textbeitrag',
  link: 'Link',
};

export function platformMeta(platform: SocialPlatform): PlatformMeta {
  return PLATFORMS[platform] ?? PLATFORMS.linkedin;
}

export function platformLabel(platform: SocialPlatform): string {
  return platformMeta(platform).label;
}

/** Post-Arten, die alle gewählten Kanäle gemeinsam unterstützen. */
export function sharedKinds(platforms: SocialPlatform[]): SocialPostKind[] {
  if (!platforms.length) return Object.keys(POST_KIND_LABEL) as SocialPostKind[];
  return platforms
    .map((p) => platformMeta(p).kinds)
    .reduce((acc, kinds) => acc.filter((k) => kinds.includes(k)));
}

export interface ChannelWarning {
  level: 'error' | 'warning';
  message: string;
}

/**
 * Prüft einen Kanal gegen Text, Titel, Medien und Kanal-Extras.
 * Spiegelt die Regeln der Server-Adapter, damit Fehler vor dem Publish sichtbar sind.
 */
export function validateChannel(params: {
  platform: SocialPlatform;
  kind: SocialPostKind;
  body: string;
  title: string;
  mediaCount: number;
  hasVideo: boolean;
  subreddit?: string;
  boardId?: string;
}): ChannelWarning[] {
  const meta = platformMeta(params.platform);
  const out: ChannelWarning[] = [];

  if (!meta.kinds.includes(params.kind)) {
    out.push({
      level: 'error',
      message: `${meta.label} unterstützt „${POST_KIND_LABEL[params.kind]}“ nicht.`,
    });
  }

  if (params.body.length > meta.textLimit) {
    out.push({
      level: 'error',
      message: `Text ist ${params.body.length - meta.textLimit} Zeichen zu lang (max. ${meta.textLimit}).`,
    });
  }

  if (meta.titleRequired && !params.title.trim()) {
    out.push({ level: 'error', message: 'Titel ist für diesen Kanal Pflicht.' });
  }

  if (params.platform === 'youtube' && params.title.length > 100) {
    out.push({ level: 'error', message: 'YouTube-Titel darf max. 100 Zeichen haben.' });
  }

  if (params.platform === 'reddit' && params.title.length > 300) {
    out.push({ level: 'error', message: 'Reddit-Titel darf max. 300 Zeichen haben.' });
  }

  if (params.platform === 'reddit' && !params.subreddit?.trim()) {
    out.push({ level: 'error', message: 'Ziel-Subreddit fehlt.' });
  }

  if (params.platform === 'pinterest' && !params.boardId?.trim()) {
    out.push({ level: 'error', message: 'Ziel-Board fehlt.' });
  }

  if (params.platform === 'x' && params.mediaCount > 4) {
    out.push({ level: 'error', message: 'X erlaubt maximal 4 Medien pro Beitrag.' });
  }

  if (meta.mediaRequired && params.mediaCount === 0) {
    out.push({ level: 'error', message: `${meta.label} benötigt mindestens ein Medium.` });
  }

  if ((params.kind === 'reel' || params.kind === 'short' || params.kind === 'video') && !params.hasVideo) {
    out.push({ level: 'error', message: 'Diese Post-Art benötigt ein Video.' });
  }

  if (params.kind === 'carousel' && params.mediaCount < 2 && params.platform !== 'linkedin') {
    out.push({ level: 'error', message: 'Carousel benötigt mindestens 2 Medien.' });
  }

  if (params.platform === 'tiktok') {
    out.push({
      level: 'warning',
      message: 'TikTok veröffentlicht privat (SELF_ONLY), bis die App bei TikTok auditiert ist.',
    });
  }

  if (params.platform === 'linkedin' && params.body.length > 1300) {
    out.push({
      level: 'warning',
      message: 'Über 1300 Zeichen kürzt LinkedIn den Text im Feed sichtbar ein.',
    });
  }

  return out;
}
